import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { Button, type ButtonProps } from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IcArrowLeftRegular } from '@kep/kcds-icons';

export interface PreviousPageButtonProps extends ButtonProps {
  /**
   * 뒤로가기 시 앱 상태·SSR 데이터를 문서 전체 로드로 다시 맞출지 여부
   *
   * @description
   * - true: 브라우저 뒤로가기(popstate) 시 Next 클라이언트 라우팅을 막고 `location.href`로
   *   동일 URL을 다시 불러와 풀 리로드합니다. (`initialGuards` 등 SSR 경로 재실행)
   * - false: `beforePopState`를 건드리지 않고 일반 `router.back()`만 사용합니다.
   */
  fullReloadOnRoute?: boolean;
}

/**
 * 이전 페이지로 이동 버튼
 *
 * @description
 * `fullReloadOnRoute`이 true일 때만 `beforePopState`를 등록합니다.
 */
export default function PreviousPageButton({
  fullReloadOnRoute = false,
  ...buttonProps
}: PreviousPageButtonProps) {
  const { t } = useTranslation();
  const router = useRouter();

  useEffect(() => {
    if (!fullReloadOnRoute) {
      return;
    }

    router.beforePopState(({ as }) => {
      window.location.href = as;
      return false;
    });

    return () => {
      router.beforePopState(() => true);
    };
  }, [router, fullReloadOnRoute]);

  const handlePreviousPage = () => router.back();

  return (
    <Button
      variant="tertiary"
      size="xl"
      startIcon={<IcArrowLeftRegular />}
      onClick={handlePreviousPage}
      {...buttonProps}
    >
      {t('ui:action.go_back')}
    </Button>
  );
}
